<script setup lang="ts">
import BaseButton from '@/components/common/BaseButton.vue'
import { PhWarningCircle, PhFolderSimple, PhArrowClockwise } from '@phosphor-icons/vue'

interface Props {
  icon?: any
  title?: string
  description?: string
  actionLabel?: string
  variant?: 'empty' | 'error' | 'loading'
}

const props = withDefaults(defineProps<Props>(), {
  title: 'No data found',
  description: 'Try adjusting your filters or reloading the page.',
  variant: 'empty',
})

const emit = defineEmits<{
  action: []
}>()
</script>

<template>
  <div class="flex flex-col items-center justify-center text-center p-8 max-w-sm mx-auto my-12 animate-fade-in">
    <div class="w-16 h-16 rounded-2xl bg-surface border border-line flex items-center justify-center mb-4">
      <div v-if="props.variant === 'loading'" class="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      <component
        v-else
        :is="props.icon || (props.variant === 'error' ? PhWarningCircle : PhFolderSimple)"
        class="w-8 h-8"
        :class="props.variant === 'error' ? 'text-accent-red' : 'text-ink-5'"
      />
    </div>

    <h3 class="text-lg font-semibold text-ink-3 mb-1">
      {{ props.variant === 'loading' ? 'Loading data...' : props.title }}
    </h3>
    <p class="text-sm text-ink-5 mb-6">
      {{ props.variant === 'loading' ? 'Please wait while we fetch the latest information.' : props.description }}
    </p>

    <BaseButton
      v-if="props.variant !== 'loading' && (props.actionLabel || props.variant === 'error')"
      variant="primary"
      @click="emit('action')"
    >
      <PhArrowClockwise v-if="props.variant === 'error' && !props.actionLabel" class="w-4 h-4 mr-2" />
      {{ props.actionLabel || 'Retry' }}
    </BaseButton>
  </div>
</template>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}
</style>
